import React, { useState } from "react";
import { BellOutlined } from "@ant-design/icons";
import { Badge, Button, Dropdown, Empty, Typography, theme } from "antd";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import styles from "./index.module.scss";

export interface NotificationItem {
  id: number;
  title: string;
  description: string;
  read: boolean;
  date?: string;
}

export interface NotificationDropdownProps {
  notifications?: NotificationItem[];
  /** Flips one notification between read and unread. */
  onToggleRead?: (id: number, read: boolean) => void;
  onMarkAllAsRead?: () => void;
}

/**
 * The bell in the header, and the panel it opens.
 *
 * The panel is three parts that do not move: a heading with the one bulk
 * action, the notifications themselves, and a way through to the full list.
 * Only the middle part scrolls, so the heading and the button below it stay
 * reachable however many notifications there are.
 */
function NotificationDropdown({
  notifications,
  onToggleRead,
  onMarkAllAsRead,
}: NotificationDropdownProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const unreadCount = notifications?.filter((item) => !item.read).length ?? 0;

  // Unread first: the panel is read from the top, and what still needs the
  // reader's attention should not sit under what does not. Order within each
  // group is left as it arrives — that is the source's, newest first.
  const ordered = notifications
    ? [...notifications].sort((a, b) => Number(a.read) - Number(b.read))
    : undefined;

  const panel = (
    <div className={styles.panel}>
      <div className={styles.head}>
        <Typography.Text strong>
          {t("pages.notificationCenter.notifications")}
        </Typography.Text>

        {/* Disabled rather than hidden: the action belongs to this panel
            whether or not there is anything to apply it to, and a control that
            comes and goes moves the heading under it. */}
        <Button
          type="link"
          size="small"
          className={styles.flushEnd}
          disabled={unreadCount === 0}
          onClick={onMarkAllAsRead}
        >
          {t("pages.notificationCenter.markAllAsRead")}
        </Button>
      </div>

      <div className={styles.list}>
        {ordered?.length ? (
          ordered.map((item) => (
            <div key={item.id} className={styles.item}>
              <div className={styles.itemHead}>
                {/* antd's own status dot, the one the tables use — red while
                    a notification is unread, and nothing once it is read. */}
                {item.read ? null : <Badge status="error" />}
                <Typography.Text strong={!item.read}>
                  {item.title}
                </Typography.Text>
              </div>

              {/* Two lines, then an ellipsis — antd's own Paragraph does the
                  clamping and the title attribute that shows the rest. */}
              <Typography.Paragraph
                type="secondary"
                className={styles.itemText}
                ellipsis={{ rows: 2, tooltip: item.description }}
              >
                {item.description}
              </Typography.Paragraph>

              <div className={styles.itemFoot}>
                <Typography.Text type="secondary" className={styles.itemDate}>
                  {item.date}
                </Typography.Text>

                <Button
                  type="link"
                  size="small"
                  className={styles.flushEnd}
                  onClick={() => onToggleRead?.(item.id, !item.read)}
                >
                  {item.read
                    ? t("pages.notificationCenter.markAsUnread")
                    : t("pages.notificationCenter.markAsRead")}
                </Button>
              </div>
            </div>
          ))
        ) : (
          <Empty
            className={styles.empty}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("pages.notificationCenter.noNotifications")}
          />
        )}
      </div>

      <div className={styles.foot}>
        {/* Navigated in the handler rather than wrapped in a link: a Button
            inside an <a> nests one interactive element in another, which a
            screen reader announces as "link, button" and the keyboard stops on
            twice. */}
        <Button
          type="primary"
          block
          onClick={() => {
            setOpen(false);
            navigate("/notification-center");
          }}
        >
          {t("pages.notificationCenter.viewAll")}
        </Button>
      </div>
    </div>
  );

  return (
    <Dropdown
      // The panel is one block of content, not a menu of choices: `dropdownRender`
      // is what antd offers for that, and it keeps the panel out of the menu
      // semantics a list of items would carry.
      popupRender={() => panel}
      trigger={["click"]}
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
    >
      {/* The badge sits on the bell, not on the button around it: a text button
          carries 8px of transparent padding on every side, so antd's own corner
          — top right of the child — left the count hanging away from the glyph.
          The offset pulls it back by exactly that padding. `size="small"` is
          antd's 14px indicator, the one its own examples use over an icon;
          `title` is what a screen reader reads in place of the number, since the
          button's label only says what the button does. */}
      <Badge
        count={unreadCount}
        size="small"
        offset={[-8, 8]}
        title={t("pages.notificationCenter.unreadCount", {
          count: unreadCount,
        })}
      >
        <Button
          type="text"
          aria-label={t("pages.notificationCenter.openNotifications")}
          icon={<BellOutlined style={{ fontSize: token.fontSizeLG }} />}
        />
      </Badge>
    </Dropdown>
  );
}

export default NotificationDropdown;
